(() => {
  const MOBILE_QUERY = '(max-width: 767px)';
  const TARGET_SELECTOR = '[data-mobile-color-swatches-target]';
  const SORT_STORAGE_KEY = 'makema:swatch-sort-mode';
  let refreshQueued = false;

  const readSortMode = () => {
    try {
      return sessionStorage.getItem(SORT_STORAGE_KEY) === 'tone' ? 'tone' : 'number';
    } catch (_error) {
      return 'number';
    }
  };

  const escapeSelector = (value) => {
    if (window.CSS?.escape) return CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const remapIds = (clone, suffix) => {
    const idMap = new Map();
    clone.querySelectorAll('input[type="radio"][name]').forEach((input) => {
      input.name = `${input.name}-${suffix}`;
    });
    clone.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      const newId = `${oldId}-${suffix}`;
      idMap.set(oldId, newId);
      element.id = newId;
    });
    clone.querySelectorAll('[for], [aria-controls], [aria-describedby], [aria-labelledby]').forEach((element) => {
      ['for', 'aria-controls', 'aria-describedby', 'aria-labelledby'].forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;
        element.setAttribute(attribute, value.split(' ').map((id) => idMap.get(id) || id).join(' '));
      });
    });
  };

  const findSource = (target) => {
    const pickerId = target.dataset.mobileColorSwatchesTarget;
    const picker = pickerId ? document.getElementById(pickerId) : null;
    const fieldset = picker?.querySelector('[data-color-option]');
    return picker && fieldset ? { picker, fieldset } : null;
  };

  const clearTarget = (target) => {
    target._makemaSource?.fieldset?.removeAttribute('data-mobile-color-swatches-source-hidden');
    target.replaceChildren();
    target._makemaSource = null;
    delete target.dataset.mobileColorSwatchesActive;
  };

  const findOriginalInput = (input, sourceFieldset) => {
    if (!input) return null;
    if (input.matches('select')) return sourceFieldset.querySelector('select');
    const optionValueId = input.getAttribute('data-option-value-id');
    return optionValueId
      ? sourceFieldset.querySelector(`input[data-option-value-id="${escapeSelector(optionValueId)}"]`)
      : null;
  };

  const proxyInput = (event, sourceFieldset) => {
    const input = event.target.closest('input[data-option-value-id], select');
    if (!input) return;
    const original = findOriginalInput(input, sourceFieldset);
    if (input.matches('select')) {
      if (original) original.value = input.value;
    } else if (original) original.checked = true;
    original?.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const proxyPreload = (event, sourceFieldset) => {
    const input = event.target.matches?.('input[data-option-value-id], select')
      ? event.target
      : event.target.closest?.('label')?.control;
    const original = findOriginalInput(input, sourceFieldset);
    if (!original || original.checked) return;
    original.dispatchEvent(new PointerEvent('pointerenter', {
      bubbles: false,
      pointerType: event.pointerType || 'mouse'
    }));
  };

  const proxySortButton = (event, sourceFieldset) => {
    const button = event.target.closest('[data-swatch-sort-mode]');
    if (!button) return;
    event.preventDefault();
    const mode = button.getAttribute('data-swatch-sort-mode');
    sourceFieldset.querySelector(`[data-swatch-sort-mode="${escapeSelector(mode)}"]`)?.click();
  };

  const renderTarget = (target) => {
    if (!window.matchMedia(MOBILE_QUERY).matches) {
      clearTarget(target);
      return;
    }
    const source = findSource(target);
    if (!source) {
      clearTarget(target);
      return;
    }
    const activeSortButton = source.fieldset.querySelector('[data-swatch-sort-mode].is-active');
    if (activeSortButton && activeSortButton.dataset.swatchSortMode !== readSortMode()) return;
    if (target._makemaSource?.fieldset === source.fieldset && target.firstElementChild && !target._makemaDirty) return;

    target._makemaSource?.fieldset?.removeAttribute('data-mobile-color-swatches-source-hidden');
    const clone = source.fieldset.cloneNode(true);
    clone.removeAttribute('data-color-option');
    clone.setAttribute('data-mobile-color-swatches-clone', '');
    clone.querySelectorAll('script').forEach((script) => script.remove());
    remapIds(clone, 'mobile-clone');
    clone.addEventListener('change', (event) => proxyInput(event, source.fieldset));
    clone.addEventListener('click', (event) => proxySortButton(event, source.fieldset));
    clone.addEventListener('pointerenter', (event) => proxyPreload(event, source.fieldset), true);
    clone.addEventListener('pointerdown', (event) => proxyPreload(event, source.fieldset));

    source.fieldset.setAttribute('data-mobile-color-swatches-source-hidden', '');
    target.replaceChildren(clone);
    target._makemaSource = source;
    target._makemaDirty = false;
    target.dataset.mobileColorSwatchesActive = 'true';
  };

  const refresh = () => document.querySelectorAll(TARGET_SELECTOR).forEach(renderTarget);
  const scheduleRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      refresh();
    });
  };

  const mediaQuery = window.matchMedia(MOBILE_QUERY);
  mediaQuery.addEventListener?.('change', scheduleRefresh);
  window.addEventListener('resize', scheduleRefresh, { passive: true });
  document.addEventListener('shopify:section:load', scheduleRefresh);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  new MutationObserver((mutations) => {
    if (mutations.every((mutation) => mutation.target.closest?.(TARGET_SELECTOR))) return;
    let needsRefresh = false;
    document.querySelectorAll(TARGET_SELECTOR).forEach((target) => {
      if (!target._makemaSource) return;
      if (!target._makemaSource.fieldset.isConnected) {
        target._makemaSource = null;
        needsRefresh = true;
        return;
      }
      const sourceFieldset = target._makemaSource.fieldset;
      const sourceChanged = mutations.some((mutation) => (
        mutation.target === sourceFieldset || sourceFieldset.contains(mutation.target)
      ));
      if (!sourceChanged) return;
      target._makemaDirty = true;
      needsRefresh = true;
    });
    if (needsRefresh) scheduleRefresh();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();